import { useCallback, useEffect, useState } from 'react';
import { AppBar } from '../components/AppBar';
import { useT } from '../i18n/I18nProvider';
import { useScanner } from '../scanner/useScanner';
import { primeAudio } from '../feedback/sound';
import { QueueStatus } from '../components/QueueStatus';
import { Icon } from '../components/icons';
import { listPartiallyScanned, mockMode } from '../api/endpoints';
import { FEATURES } from '../api/features';
import { todayIso } from '../domain/dates';

interface Props {
  onCode: (code: string) => void;
  onLogout: () => void;
  /** Přehled tras – jen pro roli MASTER, jinak se ikona v liště nezobrazí. */
  onRoutes?: () => void;
  /** Rozskenované zásilky – řádek pod skenerem. */
  onPartial: () => void;
  busy: boolean;
  error: string | null;
}

export function ScanScreen({ onCode, onLogout, onRoutes, onPartial, busy, error }: Props) {
  const { t } = useT();
  const [manual, setManual] = useState('');
  const [partial, setPartial] = useState<number | null>(null);
  const { videoRef, status, start, stop } = useScanner(onCode);

  /*
   * Počet rozskenovaných zásilek. `null` = nevíme (endpoint neběží nebo dotaz
   * selhal) → řádek se nezobrazí vůbec. Jakmile číslo dorazí, řádek zůstává
   * i při nule.
   *
   * Nula se schválně NESKRÝVÁ. Řádek, který jednou je a podruhé není, čte
   * skladník jako chybu aplikace a začne ho hledat; ještě horší je, že si
   * jeho nepřítomnost splete s „ještě se to nenačetlo“ a rozdělanou
   * objednávku začne hledat po hale. Nula je odpověď, ne prázdno.
   *
   * Selhání jen zaloguje – skener musí fungovat i bez tohohle endpointu
   * (na backendu zatím není vůbec, viz `FEATURES.overview`).
   */
  useEffect(() => {
    // `?mock=true` obchází přepínač – jinak by řádek nešlo proklikat, dokud
    // endpoint na backendu nevznikne.
    if (!FEATURES.overview && !mockMode.enabled) return;
    let live = true;
    listPartiallyScanned(todayIso())
      .then((items) => {
        if (live) setPartial(items.length);
      })
      .catch((err) => console.warn('[scan] rozskenované zásilky se nenačetly:', err));
    return () => {
      live = false;
    };
  }, []);

  // Kamera se rozjede sama – skladník nemá při 200 balících za směnu
  // mačkat „Spustit skener“ po každém balíku.
  useEffect(() => {
    void start();
    return () => stop();
  }, [start, stop]);

  const submitManual = useCallback(() => {
    const code = manual.trim();
    if (!code) return;
    primeAudio();
    setManual('');
    onCode(code);
  }, [manual, onCode]);

  return (
    <div className="app">
      <AppBar onLogout={onLogout} onRoutes={onRoutes} />

      <div className="screen-pad">
        <QueueStatus />

        <h2 className="h2">{t('scanTitle')}</h2>
        <p className="p">{t('scanHint')}</p>

        <div className="camera">
          <video ref={videoRef} playsInline muted />
        </div>

        <p className={`status${error ? ' err' : ''}`}>
          {error ??
            (busy
              ? t('scanLoading')
              : status === 'starting'
                ? t('scanStarting')
                : status === 'error'
                  ? t('scanCameraErr')
                  : '')}
        </p>

        <div className="card-plain">
          <span className="k">{t('manualTitle')}</span>
          <input
            className="input mono"
            inputMode="numeric"
            placeholder={t('manualPh')}
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitManual();
            }}
          />
          <button className="btn primary" type="button" onClick={submitManual} disabled={busy}>
            {t('manualSubmit')}
          </button>
        </div>

        {partial !== null && (
          <button className="nav-row" type="button" onClick={onPartial}>
            <Icon name="split" className="nav-row-ico" />
            <span className="nav-row-lbl">{t('partialTitle')}</span>
            {/* Nula je klidná šedá, ne amber: „nic k dodělání“ nemá volat
                po pozornosti stejně hlasitě jako tři rozdělané objednávky. */}
            <span className={`nav-row-count${partial === 0 ? ' zero' : ''}`}>{partial}</span>
            <Icon name="chevron" className="nav-row-go" />
          </button>
        )}
      </div>
    </div>
  );
}
